import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Search } from 'lucide-react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { services } from '../mock';

export default function Services() {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('All');

  const cats = ['All', ...Array.from(new Set(services.map(s => s.category)))];
  const filtered = useMemo(() => services.filter(s => {
    const matchesCat = tab === 'All' || s.category === tab;
    const matchesQ = !q || s.title.toLowerCase().includes(q.toLowerCase()) || s.summary.toLowerCase().includes(q.toLowerCase());
    return matchesCat && matchesQ;
  }), [q, tab]);

  return (
    <>
      <section className="relative pt-40 pb-20 bg-[#0B1B33] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-4">Services</div>
          <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.05] max-w-3xl">
            A full preconstruction stack for every trade.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            From conceptual budgets to bid-day takeoffs, every Core Estimation service is delivered in CSI MasterFormat,
            reviewed by a senior estimator, and turned around in 48 hours.
          </p>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2">
              {cats.map(c => (
                <button
                  key={c}
                  onClick={() => setTab(c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                    tab === c
                      ? 'bg-[#0B1B33] text-amber-400'
                      : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search services…"
                className="pl-10 h-11 rounded-xl bg-white border-slate-200"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500">No services found for “{q}”</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((s) => (
                <Link to={`/services/${s.slug}`} key={s.slug} className="group">
                  <Card className="bg-white rounded-2xl overflow-hidden border-slate-100 h-full hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                    <div className="relative h-48 overflow-hidden">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/60 to-transparent" />
                      <Badge className="absolute top-4 left-4 bg-amber-500 text-[#0B1B33] hover:bg-amber-500 font-semibold">{s.category}</Badge>
                      <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white flex items-center justify-center group-hover:bg-amber-500 transition">
                        <ArrowUpRight className="w-4 h-4 text-[#0B1B33]" />
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                          <s.icon className="w-5 h-5 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-bold text-[#0B1B33] group-hover:text-amber-600 transition">{s.title}</h3>
                      </div>
                      <p className="mt-4 text-sm text-slate-600 leading-relaxed">{s.summary}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
